import { forwardRef } from "react";

// エクスポート専用コンポーネント（画面外に非表示で配置）
const ScheduleExport = forwardRef(({ babyInfo, activities, daycareHours, activityTypes, calculateAge, isWithinDaycareHours }, ref) => {
  // 保育園時間帯の活動を抽出
  const daycareActivities = daycareHours.enabled
    ? activities.filter(
        (a) =>
          (a.type === "breastfeeding" ||
            a.type === "weaning" ||
            a.type === "medicine") &&
          isWithinDaycareHours(a.time)
      )
    : [];

  // 保育園時間帯の活動を通常リストから除外
  const regularActivities = activities.filter(
    (a) =>
      !(
        daycareHours.enabled &&
        (a.type === "breastfeeding" ||
          a.type === "weaning" ||
          a.type === "medicine") &&
        isWithinDaycareHours(a.time)
      )
  );

  // 仮想的な保育園バナーアイテムを作成
  const items = [...regularActivities];

  if (daycareHours.enabled && daycareActivities.length > 0) {
    items.push({
      id: "daycare-banner",
      time: daycareHours.start,
      type: "daycare-banner",
    });
  }

  // 時刻でソート
  items.sort((a, b) => {
    const [aHour, aMin] = a.time.split(":").map(Number);
    const [bHour, bMin] = b.time.split(":").map(Number);
    return aHour * 60 + aMin - (bHour * 60 + bMin);
  });

  // ミルク間隔を計算する関数
  const calculateMilkInterval = (activity) => {
    if (activity.type !== "breastfeeding") return "";

    const milkActivities = activities.filter((a) => a.type === "breastfeeding");
    const currentIndex = milkActivities.findIndex((m) => m.id === activity.id);

    if (currentIndex > 0) {
      const prevMilk = milkActivities[currentIndex - 1];
      const [prevHour, prevMin] = prevMilk.time.split(":").map(Number);
      const [currHour, currMin] = activity.time.split(":").map(Number);

      let totalMinutes = currHour * 60 + currMin - (prevHour * 60 + prevMin);
      if (totalMinutes < 0) totalMinutes += 24 * 60;

      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;

      if (hours > 0 && minutes > 0) {
        return `${hours}時間${minutes}分後`;
      } else if (hours > 0) {
        return `${hours}時間後`;
      } else {
        return `${minutes}分後`;
      }
    } else if (currentIndex === 0 && milkActivities.length > 1) {
      const lastMilk = milkActivities[milkActivities.length - 1];
      const [lastHour, lastMin] = lastMilk.time.split(":").map(Number);
      const [firstHour, firstMin] = activity.time.split(":").map(Number);

      let totalMinutes = firstHour * 60 + firstMin - (lastHour * 60 + lastMin);
      if (totalMinutes < 0) totalMinutes += 24 * 60;

      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;

      if (hours > 0 && minutes > 0) {
        return `${hours}時間${minutes}分後`;
      } else if (hours > 0) {
        return `${hours}時間後`;
      } else {
        return `${minutes}分後`;
      }
    }
    return "";
  };

  return (
    <div ref={ref} className="bg-white w-[480px] px-6 py-5">
      {/* タイトル部分 */}
      <div className="flex items-center gap-2 mb-4">
        <img src="/mokubaICOOON.png" alt="装飾" className="w-3.5 h-3.5" />
        <div>
          <h1 className="text-sm font-bold text-gray-800">{babyInfo.nickname}</h1>
          <p className="text-xs text-gray-600">{calculateAge(babyInfo.birthday)}</p>
        </div>
      </div>

      {/* スケジュールリスト */}
      <div className="relative">
        <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200"></div>

        {items.map((item) => {
          // 保育園バナー
          if (item.type === "daycare-banner") {
            return (
              <div
                key={item.id}
                className="bg-amber-50 border-l-6 border-amber-400 rounded-r-lg p-4 mb-3 ml-6"
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-lg">🏫</span>
                  <span className="text-sm font-semibold text-amber-800 whitespace-nowrap">
                    保育園 ({daycareHours.start} 〜 {daycareHours.end})
                  </span>
                </div>
                <div className="space-y-2">
                  {daycareActivities.map((da) => {
                    const { label, color, icon } = activityTypes[da.type];
                    const milkInterval = calculateMilkInterval(da);

                    return (
                      <div key={da.id} className="relative flex items-start gap-2">
                        <div className={`w-6 h-6 ${color} rounded flex items-center justify-center text-white flex-shrink-0 shadow-sm mt-1`}>
                          <span className="text-xs">{icon}</span>
                        </div>
                        <div className="flex-1 bg-white border border-gray-200 rounded-lg p-2 shadow-sm">
                          <div className="flex items-center gap-1.5 flex-wrap whitespace-nowrap">
                            <span className="text-sm font-semibold text-gray-800">{da.time}</span>
                            <span className="text-xs font-semibold text-gray-800">{label}</span>
                            {da.amount && (
                              <span className="text-sm font-semibold text-red-400">{da.amount}</span>
                            )}
                            {milkInterval && (
                              <span className="text-xs text-red-400 font-medium">({milkInterval})</span>
                            )}
                            {da.note && (
                              <span className="text-xs text-cyan-600">{da.note}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          }

          // 通常の活動
          const activity = item;
          const { label, color, icon } = activityTypes[activity.type];
          const milkInterval = calculateMilkInterval(activity);

          return (
            <div key={activity.id} className="relative flex items-start gap-2 mb-3 pr-4">
              <div className={`w-6 h-6 ${color} rounded flex items-center justify-center text-white z-10 flex-shrink-0 shadow-sm mt-1`}>
                <span className="text-xs">{icon}</span>
              </div>
              <div className="flex-1 bg-white border border-gray-200 rounded-lg p-2 shadow-sm">
                <div className="flex items-center gap-1.5 flex-wrap whitespace-nowrap">
                  <span className="text-sm font-semibold text-gray-800">{activity.time}</span>
                  <span className="text-xs font-semibold text-gray-800">{label}</span>
                  {activity.amount && (
                    <span className="text-sm font-semibold text-red-400">{activity.amount}</span>
                  )}
                  {milkInterval && (
                    <span className="text-xs text-red-400 font-medium">({milkInterval})</span>
                  )}
                  {activity.note && (
                    <span className="text-xs text-cyan-600">{activity.note}</span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
});

ScheduleExport.displayName = 'ScheduleExport';

export default ScheduleExport;
